import {
  InitialTableState,
  getPaginationRowModel,
} from "@tanstack/react-table";
import * as React from "react";

import { TEN, TWENTY_FIVE, ZERO } from "@/app/constants/constants";
import type { ResponsePagination } from "@/components/pagination/hooks/usePagination";

type DataTablePageIndexSetter = (pageIndex: number) => void;

interface UseDataTableServerStateProps {
  dataLength: number;
  initialState?: InitialTableState;
  isTablePaginated: boolean;
  pagination?: ResponsePagination;
  legacyPagination?: ResponsePagination;
}

interface DataTablePaginationOptions {
  getPaginationRowModel?: ReturnType<typeof getPaginationRowModel>;
  initialState?: InitialTableState;
  manualPagination?: boolean;
  pageCount?: number;
}

interface UseDataTableServerStateReturn {
  currentPage?: number;
  hasExternalPagination: boolean;
  placeholderLimit: number;
  resolvedPagination?: ResponsePagination;
  shouldRenderPaginator: boolean;
  shouldSyncClientPageIndex: boolean;
  tablePaginationOptions: DataTablePaginationOptions;
  tablePaginationState?: {
    pageIndex: number;
    pageSize: number;
  };
  totalPages?: number;
  handlePageChange: (
    pageNumber: number,
    setClientPageIndex?: DataTablePageIndexSetter,
  ) => void;
  handlePageSizeChange?: (pageSize: number) => void;
}

/**
 * Separates server-driven pagination from client-side table mechanics.
 *
 * @param {UseDataTableServerStateProps} props - Data-table pagination inputs.
 * @returns {UseDataTableServerStateReturn} Normalized pagination state for shared table rendering.
 */
export const useDataTableServerState = (
  props: UseDataTableServerStateProps,
): UseDataTableServerStateReturn => {
  const {
    dataLength,
    initialState,
    isTablePaginated,
    pagination,
    legacyPagination,
  } = props;
  const resolvedPagination = pagination ?? legacyPagination;
  const hasExternalPagination = Boolean(resolvedPagination);
  const shouldSyncClientPageIndex = !hasExternalPagination;
  const placeholderLimit = resolvedPagination?.limit ?? TWENTY_FIVE;
  const shouldRenderPaginator =
    (resolvedPagination?.totalResult ?? dataLength) > TEN;

  const tablePaginationState = React.useMemo(() => {
    if (!hasExternalPagination) {
      return undefined;
    }

    return {
      pageIndex: Math.max((resolvedPagination?.currentPage ?? 1) - 1, ZERO),
      pageSize: resolvedPagination?.limit ?? TWENTY_FIVE,
    };
  }, [
    hasExternalPagination,
    resolvedPagination?.currentPage,
    resolvedPagination?.limit,
  ]);

  const tablePaginationOptions =
    React.useMemo((): DataTablePaginationOptions => {
      if (!isTablePaginated) {
        return {};
      }

      if (hasExternalPagination) {
        return {
          manualPagination: true,
          pageCount: resolvedPagination?.totalPages ?? -1,
        };
      }

      return {
        initialState: {
          ...initialState,
          pagination: {
            pageSize: TWENTY_FIVE,
          },
        },
        getPaginationRowModel: getPaginationRowModel(),
      };
    }, [
      hasExternalPagination,
      initialState,
      isTablePaginated,
      resolvedPagination?.totalPages,
    ]);

  const handlePageChange = React.useCallback(
    (
      pageNumber: number,
      setClientPageIndex?: DataTablePageIndexSetter,
    ): void => {
      if (shouldSyncClientPageIndex) {
        setClientPageIndex?.(pageNumber - 1);
      }

      resolvedPagination?.handlePageUpdate(pageNumber);
    },
    [resolvedPagination, shouldSyncClientPageIndex],
  );

  const handlePageSizeChange = React.useCallback(
    (newPageSize: number): void => {
      resolvedPagination?.handleLimitUpdate(newPageSize);
    },
    [resolvedPagination],
  );

  return {
    currentPage: resolvedPagination?.currentPage,
    hasExternalPagination,
    placeholderLimit,
    resolvedPagination,
    shouldRenderPaginator,
    shouldSyncClientPageIndex,
    tablePaginationOptions,
    tablePaginationState,
    totalPages: resolvedPagination?.totalPages,
    handlePageChange,
    handlePageSizeChange: hasExternalPagination
      ? handlePageSizeChange
      : undefined,
  };
};
